import { Account, Positions, useAccountStore } from '@/store/use-account-store';
import { OrderStatus, TransferType } from '@/types/enums';
import {
  FilledTransfer,
  NewTransfer,
  useTransfersStore,
} from '@/store/use-transfers-store';
import { parseDecimalToBigInt } from '@/utils/value-format';

import { TransferResponse } from '@/features/account/api/get-transfers';

import { Order, useOrdersStore } from '@/store/use-orders-store';

import {
  COLLATERAL_DECIMALS,
  CUM_FUNDING_DECIMALS,
  getAllMarketStoreStates,
  MarketStore,
  useMarketStore,
} from '@/store/use-markets-store';
import { AccountResponse } from '@/types';
import { OrderResponse } from '@/features/account/api/get-orders';
import { getUpdatedAccountOnFill } from '@/features/account/utils/math';

export const handleTransferForStore = (transfer: TransferResponse) => {
  const { setTransfer, addNewDeposit } = useTransfersStore.getState();

  const {
    address,
    accounts,
    accountsLastUpdated,
    setAccount,
    setAccountLastUpdated,
  } = useAccountStore.getState();

  const account = accounts[address ?? ''];
  const accountLastUpdated = accountsLastUpdated[address ?? ''];

  if (transfer.status === OrderStatus.REJECTED) {
    setTransfer(transfer.id, transfer);
  } else {
    const parsedTransfer = {
      ...transfer,
      size: parseDecimalToBigInt(transfer.size, COLLATERAL_DECIMALS),
    };

    setTransfer(transfer.id, parsedTransfer);

    if (
      transfer.orderType === TransferType.DEPOSIT &&
      transfer.status === OrderStatus.FILLED
    ) {
      const filledDeposit = parsedTransfer as FilledTransfer;
      addNewDeposit(filledDeposit);

      if (filledDeposit.postTime > accountLastUpdated) {
        setAccount({
          ...account,
          collateral: account.collateral + filledDeposit.size,
        });
        setAccountLastUpdated(filledDeposit.postTime);
      }
    } else if (
      transfer.orderType === TransferType.WITHDRAW &&
      transfer.status === OrderStatus.NEW
    ) {
      const newWithdraw = parsedTransfer as NewTransfer;

      if (newWithdraw.postTime > accountLastUpdated) {
        setAccount({
          ...account,
          collateral: account.collateral - newWithdraw.size,
        });
        setAccountLastUpdated(newWithdraw.postTime);
      }
    }
  }
};

export const handleAccountForStore = (res: AccountResponse) => {
  const {
    collateral,
    positions,
    leverages,
    time,
    twitterUsername,
    discordUsername,
  } = res;
  const { setAccount, setAccountLastUpdated, accountsLastUpdated, address } =
    useAccountStore.getState();
  const accountLastUpdated = accountsLastUpdated[address ?? ''];
  if (time <= accountLastUpdated) return;

  const account: Account = {
    collateral: parseDecimalToBigInt(collateral, COLLATERAL_DECIMALS),
    positions: positions.reduce((acc: Positions, pos: any) => {
      const { marketSpec } = useMarketStore(pos.symbol).getState();
      acc[pos.symbol] = {
        isLong: pos.isLong,
        size: parseDecimalToBigInt(pos.size, marketSpec.sizeDecimals),
        cost:
          parseDecimalToBigInt(pos.entryPrice, marketSpec.priceDecimals) *
          parseDecimalToBigInt(pos.size, marketSpec.sizeDecimals),
        entryFunding: parseDecimalToBigInt(
          pos.entryFunding,
          CUM_FUNDING_DECIMALS,
        ),
        initMarginRatio: parseDecimalToBigInt(pos.initMarginRatio, 4n),
      };
      return acc;
    }, {} as Positions),
    leverages,
    connections: {
      twitter: twitterUsername,
      discord: discordUsername,
    },
  };

  setAccount(account);
  setAccountLastUpdated(time);

  positions.forEach((pos) => {
    const { marketData, marketSpec, setMarketData } = useMarketStore(
      pos.symbol,
    ).getState();
    setMarketData({
      ...marketData,
      indexPrice: parseDecimalToBigInt(
        pos.indexPrice,
        marketSpec.priceDecimals,
      ),
      markPrice: parseDecimalToBigInt(pos.markPrice, marketSpec.priceDecimals),
    });
  });
};

type HandleOrderForStoreOptions = {
  skipAccountUpdate?: boolean;
};

export const handleOrderForStore = (
  orderResponse: OrderResponse,
  options?: HandleOrderForStoreOptions,
) => {
  try {
    const marketStores = getAllMarketStoreStates();
    const { setOrder } = useOrdersStore.getState();

    const order = orderResponseToOrder(orderResponse, marketStores);
    setOrder(order);

    if (options?.skipAccountUpdate) return order;

    // Update account with filled order (primarily handle the case if ws misses a fill)
    const {
      address,
      accounts,
      accountsLastUpdated,
      setAccount,
      setAccountLastUpdated,
    } = useAccountStore.getState();

    const account = accounts[address ?? ''];
    const accountLastUpdated = accountsLastUpdated[address ?? ''];

    if (
      order.status === OrderStatus.FILLED &&
      order.lastFilledTime > accountLastUpdated
    ) {
      const updatedAccount = getUpdatedAccountOnFill(
        order.symbol,
        order.isBuy,
        order.lastFilledSize,
        order.lastFilledPrice,
        order.realizedPnl,
        order.initMarginRatio,
        account,
        marketStores[order.symbol].marketData,
      );
      setAccount(updatedAccount);
      setAccountLastUpdated(order.lastFilledTime);
    }
    return order;
  } catch (error) {
    console.error(error);
  }
};

export const orderResponseToOrder = (
  orderResponse: OrderResponse,
  marketStores: Record<string, MarketStore>,
): Order => {
  const { marketSpec } = marketStores[orderResponse.symbol];
  switch (orderResponse.status) {
    case OrderStatus.NEW:
      return {
        ...orderResponse,
        size: parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals),
        limitPrice: parseDecimalToBigInt(
          orderResponse.limitPrice,
          marketSpec.priceDecimals,
        ),
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice: orderResponse.tpPrice
          ? parseDecimalToBigInt(
              orderResponse.tpPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
        slPrice: orderResponse.slPrice
          ? parseDecimalToBigInt(
              orderResponse.slPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
      };
    case OrderStatus.FILLED:
      return {
        ...orderResponse,
        size: parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals),
        limitPrice: parseDecimalToBigInt(
          orderResponse.limitPrice,
          marketSpec.priceDecimals,
        ),
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice: orderResponse.tpPrice
          ? parseDecimalToBigInt(
              orderResponse.tpPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
        slPrice: orderResponse.slPrice
          ? parseDecimalToBigInt(
              orderResponse.slPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
        lastFilledSize: parseDecimalToBigInt(
          orderResponse.lastFilledSize,
          marketSpec.sizeDecimals,
        ),
        lastFilledPrice: parseDecimalToBigInt(
          orderResponse.lastFilledPrice,
          marketSpec.priceDecimals,
        ),
        avgFilledPrice: parseDecimalToBigInt(
          orderResponse.avgFilledPrice,
          marketSpec.priceDecimals,
        ),
        realizedPnl: parseDecimalToBigInt(
          orderResponse.realizedPnl,
          COLLATERAL_DECIMALS,
        ),
        settledFunding: parseDecimalToBigInt(
          orderResponse.settledFunding,
          COLLATERAL_DECIMALS,
        ),
        fees: parseDecimalToBigInt(orderResponse.fees, COLLATERAL_DECIMALS),
        markPrice: parseDecimalToBigInt(
          orderResponse.markPrice,
          marketSpec.priceDecimals,
        ),
      };
    case OrderStatus.CANCELLED:
      return {
        ...orderResponse,
        size: parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals),
        limitPrice: parseDecimalToBigInt(
          orderResponse.limitPrice,
          marketSpec.priceDecimals,
        ),
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice: orderResponse.tpPrice
          ? parseDecimalToBigInt(
              orderResponse.tpPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
        slPrice: orderResponse.slPrice
          ? parseDecimalToBigInt(
              orderResponse.slPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
      };
    case OrderStatus.REJECTED:
      return {
        ...orderResponse,
        size: parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals),
        limitPrice: parseDecimalToBigInt(
          orderResponse.limitPrice,
          marketSpec.priceDecimals,
        ),
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice: orderResponse.tpPrice
          ? parseDecimalToBigInt(
              orderResponse.tpPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
        slPrice: orderResponse.slPrice
          ? parseDecimalToBigInt(
              orderResponse.slPrice,
              marketSpec.priceDecimals,
            )
          : undefined,
      };
  }
};
